import { useRef } from 'react';
import type { FormFieldDef } from '@hcc/types';
import { Paperclip, X } from 'lucide-react';
import { readAsDataUrl } from '../../lib/upload';

export interface PendingFile {
  filename: string;
  /** data: URL (base64); the server strips the prefix. */
  base64: string;
}

interface FileUploadFieldProps {
  field: FormFieldDef;
  files: PendingFile[];
  onChange: (next: PendingFile[]) => void;
}

/** Real (non-preview) file input that captures uploads as base64. */
export function FileUploadField({ field, files, onChange }: FileUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const accept = (field.accept ?? []).map((e) => '.' + e).join(',');

  async function onPick(list: FileList | null) {
    if (!list || list.length === 0) return;
    const read = await Promise.all(
      Array.from(list).map(async (f) => ({ filename: f.name, base64: await readAsDataUrl(f) })),
    );
    onChange(field.multiple ? [...files, ...read] : read.slice(0, 1));
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <fieldset className="block text-sm">
      <span className="font-medium text-fg-muted">
        {field.label}
        {field.required ? <span className="text-danger"> *</span> : null}
      </span>
      <div className="mt-1.5 space-y-2">
        {files.map((f, i) => (
          <div key={i} className="flex items-center gap-2 rounded-token-sm border border-line/10 bg-surface-2 px-2.5 py-1.5 text-sm">
            <Paperclip className="h-4 w-4 text-fg-dim" />
            <span className="min-w-0 flex-1 truncate text-fg">{f.filename}</span>
            <button type="button" onClick={() => onChange(files.filter((_, j) => j !== i))}
              className="rounded-token-sm p-1 text-fg-dim hover:text-danger" aria-label="Remove file">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        <input
          ref={inputRef}
          type="file"
          accept={accept || undefined}
          multiple={field.multiple}
          onChange={(e) => onPick(e.target.files)}
          className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-token-sm file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:text-fg-muted"
        />
      </div>
      <span className="mt-1 block text-xs text-fg-dim">
        {field.help ? field.help + ' ' : ''}
        Allowed: {(field.accept ?? []).join(', ') || 'any safe file type'}
      </span>
    </fieldset>
  );
}
